import React, { useState, useEffect } from "react";
import { learningsApi } from "../lib/api";
import { toast } from "sonner";
import { GraduationCap, Trash } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

export default function InstitutionalLearningsPage() {
  const [learnings, setLearnings] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await learningsApi.list();
      setLearnings(data);
    } catch { toast.error("Failed to load learnings"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const demote = async (id) => {
    if (!window.confirm("Demote this institutional learning?")) return;
    try {
      await learningsApi.demote(id);
      setLearnings((l) => l.filter((x) => x.id !== id));
      toast.success("Demoted");
    } catch { toast.error("Failed to demote"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1">Institutional Knowledge</p>
        <h1 className="font-display text-[22px] font-bold text-slate-900 tracking-tight">
          Learnings <span className="text-slate-400 font-normal">({learnings.length})</span>
        </h1>
        <p className="text-[13px] text-slate-500 mt-1">
          Operator-validated facts. These feed directly into Hermes's retrieval chain at the highest priority.
        </p>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 py-12 text-center">Loading learnings…</div>
      ) : learnings.length === 0 ? (
        <div className="text-center py-16">
          <GraduationCap size={28} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No institutional learnings yet.</p>
          <p className="text-xs text-slate-400 mt-1">
            Promote working theories from the intelligence feed to create learnings.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {learnings.map((l) => (
            <article key={l.id} className="border border-slate-200 border-l-4 border-l-emerald-500 bg-white rounded-lg hover:shadow-sm transition-shadow">
              <div className="px-5 py-4">
                <div className="flex items-center gap-2 mb-2.5">
                  <Badge variant="learning">INSTITUTIONAL</Badge>
                  <span className={`font-mono text-[13px] font-bold ml-auto tabular-nums ${
                    l.confidence >= 80 ? "text-emerald-600" :
                    l.confidence >= 60 ? "text-amber-600" : "text-rose-600"
                  }`}>
                    {l.confidence}<span className="text-[11px] font-normal text-slate-400">/100</span>
                  </span>
                </div>
                <h3 className="text-[15px] font-semibold text-slate-900 mb-1.5">{l.title}</h3>
                <p className="text-[13px] text-slate-600 leading-relaxed">{l.learning}</p>
                {l.tags?.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap mt-2.5">
                    {l.tags.map((t) => (
                      <span key={t} className="text-[11px] font-mono uppercase tracking-wider text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                  <span className="text-[11px] font-mono text-slate-400">
                    {new Date(l.promotion_date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                  <span className="text-[11px] font-mono text-slate-400">Priority {l.retrieval_priority}</span>
                  <Button variant="outline" size="sm" onClick={() => demote(l.id)}
                    className="ml-auto text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200 h-7 text-[12px]">
                    <Trash size={12} /> Demote
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
